import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/Button';
import { AvailabilityOwner, useAvailabilityRuleMutations, useAvailabilityRules } from '../hooks';
import { AvailabilityRule, SchedulingApiError } from '../types';
import { WEEKDAYS, fromApiTime, toApiTime, weekdayName } from '../utils';

interface WeeklyHoursEditorProps {
  owner: AvailabilityOwner;
  ownerId: string;
}

const addHours = (time: string, hours: number): string => {
  const [h, m] = time.split(':').map(Number);
  const total = Math.min(h * 60 + m + hours * 60, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const TimeRow: React.FC<{
  rule: AvailabilityRule;
  onSave: (start: string, end: string) => Promise<void>;
  onRemove: () => void;
}> = ({ rule, onSave, onRemove }) => {
  const { t } = useTranslation();
  const [start, setStart] = useState(fromApiTime(rule.start_time));
  const [end, setEnd] = useState(fromApiTime(rule.end_time));
  const [saving, setSaving] = useState(false);
  const dirty = start !== fromApiTime(rule.start_time) || end !== fromApiTime(rule.end_time);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(start, end);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="time"
        value={start}
        onChange={e => setStart(e.target.value)}
        aria-label={t('schedulingAdmin.hours.from')}
        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
      />
      <span className="text-gray-500">–</span>
      <input
        type="time"
        value={end}
        onChange={e => setEnd(e.target.value)}
        aria-label={t('schedulingAdmin.hours.to')}
        className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
      />
      {dirty && (
        <Button size="sm" onClick={handleSave} loading={saving} disabled={!start || !end}>
          {t('common.save')}
        </Button>
      )}
      <Button size="sm" variant="ghost" title={t('common.remove')} onClick={onRemove}>
        <span className="material-icons text-base">close</span>
      </Button>
    </div>
  );
};

export const WeeklyHoursEditor: React.FC<WeeklyHoursEditorProps> = ({ owner, ownerId }) => {
  const { t, i18n } = useTranslation();
  const { data: rules = [], isLoading } = useAvailabilityRules(owner, ownerId);
  const mutations = useAvailabilityRuleMutations(owner, ownerId);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(
        err instanceof SchedulingApiError ? err.message : t('schedulingAdmin.errors.generic')
      );
    }
  };

  const addInterval = (weekday: number, dayRules: AvailabilityRule[]) => {
    const last = dayRules
      .filter(rule => !rule.is_closed)
      .sort((a, b) => (a.end_time ?? '').localeCompare(b.end_time ?? ''))
      .pop();
    const start = last ? fromApiTime(last.end_time) : '09:00';
    const end = last ? addHours(start, 3) : '17:00';
    run(() =>
      mutations.create.mutateAsync({
        weekday,
        is_closed: false,
        start_time: toApiTime(start),
        end_time: toApiTime(end),
      })
    );
  };

  if (isLoading) return <p className="text-sm text-gray-500">{t('common.loading')}</p>;

  const isResource = owner === 'resource';

  return (
    <div className="space-y-3">
      {WEEKDAYS.map(weekday => {
        const dayRules = rules
          .filter(rule => rule.weekday === weekday)
          .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''));
        const closed = dayRules.some(rule => rule.is_closed);
        const intervals = dayRules.filter(rule => !rule.is_closed);

        return (
          <div
            key={weekday}
            className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
          >
            <div className="w-32 pt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
              {weekdayName(weekday, i18n.language)}
            </div>
            <div className="flex-1 space-y-2">
              {dayRules.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isResource
                    ? t('schedulingAdmin.hours.inherit')
                    : t('schedulingAdmin.hours.closed')}
                </p>
              )}
              {closed &&
                dayRules.map(rule => (
                  <div key={rule.id} className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {isResource
                        ? t('schedulingAdmin.hours.off')
                        : t('schedulingAdmin.hours.closed')}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      title={t('common.remove')}
                      onClick={() => run(() => mutations.remove.mutateAsync(rule.id))}
                    >
                      <span className="material-icons text-base">close</span>
                    </Button>
                  </div>
                ))}
              {intervals.map(rule => (
                <TimeRow
                  key={`${rule.id}-${rule.start_time}-${rule.end_time}`}
                  rule={rule}
                  onSave={(start, end) =>
                    run(() =>
                      mutations.update.mutateAsync({
                        ruleId: rule.id,
                        data: { start_time: toApiTime(start), end_time: toApiTime(end) },
                      })
                    )
                  }
                  onRemove={() => run(() => mutations.remove.mutateAsync(rule.id))}
                />
              ))}
              {!closed && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => addInterval(weekday, dayRules)}
                  >
                    {t('schedulingAdmin.hours.addRange')}
                  </Button>
                  {isResource && dayRules.length === 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        run(() => mutations.create.mutateAsync({ weekday, is_closed: true }))
                      }
                    >
                      {t('schedulingAdmin.hours.markOff')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
